/**
 * Google Ads prohibits phone numbers in ad text (#6510).
 *
 * A port of backend/app/utils/ad_copy_policy.py. The server stays authoritative -- the ad PUT
 * returns 422 -- so drift here degrades to a later message, never to a policy hole. The corpora in
 * ad-copy-policy.test.ts and test_ad_copy_policy.py are kept identical on purpose.
 */

const SPACED_RANGE = /\s[-–—.]\s/;
const CANDIDATE = /(?<![A-Za-z0-9])\(?\+?\d[\d \u00a0.()-]*\d(?![A-Za-z0-9])/g;
const CUE = /(?:call|phone|telephone|tel|dial|freephone|hotline|whatsapp|sms|text|txt|mobile)[\s:.-]*$/i;
const GROUPED_NUMBER = /^\d{1,3}([.,]\d{3})+$/;
const NON_PHONE_PREFIXES = new Set(['£', '$', '€', '¥', '₹', '#']);

// 9 clears every realistic date, time, price and version shape, which top out at 8 digits.
// A leading "+" or an adjacent cue word disambiguates enough to drop to 7.
const NATIONAL_MIN_DIGITS = 9;
const CUED_MIN_DIGITS = 7;

function digitCount(value: string): number {
  return (value.match(/\d/g) ?? []).length;
}

/** The phone-number-shaped run in `text`, or null. */
export function findPhoneNumber(text: string): string | null {
  for (const chunk of text.split(SPACED_RANGE)) {
    CANDIDATE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = CANDIDATE.exec(chunk)) !== null) {
      const run = match[0];
      const preceding = match.index > 0 ? chunk[match.index - 1] : '';
      const following = chunk[match.index + run.length] ?? '';
      if (NON_PHONE_PREFIXES.has(preceding) || following === '%') continue;
      if (GROUPED_NUMBER.test(run.trim())) continue;
      const minimum =
        run.trimStart().startsWith('+') || CUE.test(chunk.slice(0, match.index))
          ? CUED_MIN_DIGITS
          : NATIONAL_MIN_DIGITS;
      if (digitCount(run) >= minimum) return run.trim();
    }
  }
  return null;
}

export function containsPhoneNumber(text: string): boolean {
  return findPhoneNumber(text) !== null;
}
